import { TabItem } from '@components/v2/ui/Tabs/tabs.types';
import { Children, ReactElement, ReactNode, isValidElement, useEffect } from 'react';
import { ClarityEvents } from '../../../../../../../lib/clarity/types/clarityEvents.types';
import { captureEventWithName } from '../../../../../../../lib/clarity/utils/captureEvent.utils';
import { useObjectContext } from '../../../hooks/useObjectContext';
import { ObjectTab, ObjectTabProps } from '../components/ObjectTab';
import { activateTab } from '../objectTabs.utils';

interface UseObjectTabsParams {
  children: ReactNode;
}

export function useObjectTabs({ children }: UseObjectTabsParams) {
  const { activeTab, setActiveTab } = useObjectContext();

  const childTabs = Children.toArray(children).filter(
    (child): child is ReactElement<ObjectTabProps> =>
      isValidElement(child) && child.type === ObjectTab,
  );

  const items: TabItem[] = childTabs.map((tab) => ({
    id: tab.props.tabKey,
    label: tab.props.label,
    secondaryLabel: tab.props.count,
    endIcon: tab.props.endIcon,
    tooltip: tab.props.tooltip,
    testId: tab.props.testId,
  }));

  const firstTabId = items[0]?.id;
  const hasActiveTab = items.some((item) => item.id === activeTab);

  useEffect(() => {
    if (!hasActiveTab && firstTabId) {
      setActiveTab(firstTabId);
    }
  }, [hasActiveTab, firstTabId, setActiveTab]);

  const handleSelect = (id: string) => {
    const behavior = childTabs.find((tab) => tab.props.tabKey === id)?.props.behavior ?? 'swap';
    captureEventWithName(ClarityEvents.OBJECT_SCREEN_CHANGED_TAB_WITH_NAME, id);
    activateTab(behavior, id, setActiveTab);
  };

  return { items, activeTab, handleSelect };
}
